{
  "common": {
    "header": {
      "siteNavAria": "Site navigation",
      "homeAria": "Kirigami home",
      "navAria": "Main navigation",
      "mobileNavAria": "Mobile navigation",
      "menuToggleAria": "Toggle navigation menu",
      "read": "Read",
      "write": "Write",
      "search": "Search",
      "library": "Library",
      "about": "About us",
      "pricing": "Pricing",
      "support": "Support",
      "plans": "Plans and pricing",
      "signIn": "Sign in"
    },
    "footer": {
      "brandName": "Kirigami",
      "addressStreet": "Rua Pedro Nunes, edifício C. Alto de São João.",
      "addressCity": "3030-199 Coimbra, Portugal",
      "vat": "NIPC/VAT: {number}",
      "productTitle": "Product",
      "howItWorks": "How it works",
      "features": "Features",
      "pricing": "Pricing",
      "support": "Support",
      "about": "About us",
      "legalTitle": "Legal",
      "terms": "Terms & conditions",
      "privacy": "Privacy policy",
      "refund": "Refund policy",
      "cookies": "Cookies",
      "accessibility": "Accessibility statement",
      "socialAria": "Kirigami on social media",
      "linkedinAria": "Kirigami on LinkedIn",
      "instagramAria": "Kirigami on Instagram",
      "blueskyAria": "Kirigami on Bluesky",
      "rights": "All rights reserved © {year} Kirigami paper LDA"
    }
  },
  "about": {
    "title": "About Kirigami",
    "vision": {
      "eyebrow": "Our vision",
      "text": "One place where a scientist's work lives, develops, and comes out in whatever format the world asks for next."
    },
    "mission": {
      "eyebrow": "Our mission",
      "text": "Kirigami exists to help scientists bridge the gap between knowing the science and knowing how to tell its story.",
      "belief": "We believe the ability to write should not be the barrier to advancing science."
    },
    "origin": {
      "title": "Built from a problem we faced ourselves",
      "frustration": "Kirigami began with our co-founder's own frustration. Writing up her PhD research, she needed credible guidance on how to tell the story of the work, not a tool that would write it for her. The writing tools available did the opposite: they generated the text instead of helping her write it better.",
      "hollow": "Those tools do not understand the story behind the research: why the question was chosen, how it developed, and what the results really show beyond the numbers. The words may sound polished, but the meaning is vague and hollow.",
      "built": "Our founders and the team spent a year working behind the scenes, building Kirigami in a considered way, to guide and support scientists rather than replace them with black boxes. That is what Kirigami is built for: giving a safe structure to scientific writing."
    },
    "philosophy": {
      "titleLine1": "Many jobs.",
      "titleLine2": "One author.",
      "text": "A paper is the end of a long list of jobs. You formed the hypothesis, designed the experiment, searched and read the literature, collected the data, ran the analysis, built the figures and decided what it all means. Every one of those is yours. Some of them, though, have a stretch where a well-built tool can help without taking over your authorship. That is the ground Kirigami is building on, one tool at a time.",
      "principlesTitle": "Six principles every Kirigami tool follows."
    },
    "principles": {
      "authorship": {
        "title": "Human authorship",
        "body": "Our first principle. Ideas, data, claims, interpretations and the credit belong to the researcher. From the microscope to the sequencer, technology has helped research advance, and the decisions have always stayed human. Kirigami is building tools for that same partnership: the technology supports, the researcher decides."
      },
      "integrity": {
        "title": "Scientific integrity",
        "body": "Faster research is only worth having if it can still be trusted. Critical thinking is not a step to skip: it is where the science happens. The shortcuts that damage it are well known: a citation that does not exist, a sentence that claims more than the data show, etc. Every Kirigami tool is built to close that door. It works from your own inputs: your text, your data and your library. It highlights problems and offers suggestions, but the thinking and the decision stay with you."
      },
      "transparency": {
        "title": "Transparency",
        "body": "Every suggestion traces back to something you wrote or a paper from your library, and it comes with its reasoning. You decide what to keep or dismiss, and nothing in your document changes unless you change it. That is what makes Kirigami simple to declare under the AI policies of journals and funders."
      },
      "privacy": {
        "title": "Privacy and confidentiality",
        "body": "Unpublished results, datasets and early ideas are the most sensitive material a researcher has. Your work stays yours: encrypted in transit and at rest, accessible only to you, never shared and never used to train models (from third parties or our own)."
      },
      "learning": {
        "title": "Learn as you go",
        "body": "Good guidance should make you a better writer, not a dependent one. Every note explains what is missing, why it matters and how you might address it, so each manuscript teaches you something the next one will not need."
      },
      "collaboration": {
        "title": "Collaboration",
        "body": "Science is collaborative: most research is done in teams. Every Kirigami tool is built for the whole team, not just one author. Co-authors work on the same manuscript and see the same feedback. You can assign tasks, follow progress and share knowledge bases across the team, so everyone works from the same sources and the paper stays one coherent piece of work."
      }
    },
    "name": {
      "title": "Why “Kirigami”?",
      "word": "切り紙",
      "caption": "The art of folding and cutting paper",
      "art": "Kirigami is the Japanese art of folding and cutting a flat sheet of paper into a three-dimensional structure. The artist cannot see the finished object while working: the shape is already implied and a precise sequence of folds and cuts reveals it.",
      "paper": "Your paper already exists, in your head and in your data. The hard part is cutting, ordering and connecting it.",
      "reveal": "Kirigami helps you reveal the structure. It does not invent substance."
    },
    "researchers": {
      "title": "Built for researchers",
      "lead": "Researchers should spend their time thinking about science, not fighting with the writing process.",
      "friction": "Scientific writing requires researchers to transform complex research into a clear and structured narrative. Kirigami is designed to reduce this friction.",
      "guidance": "Through structured guidance and manuscript analysis, Kirigami helps researchers identify weaknesses, improve clarity and communicate their research more effectively while remaining responsible for the scientific content."
    },
    "cta": {
      "title": "Your research. Your words. Better communicated.",
      "button": "Analyse your manuscript"
    }
  },
  "dashboard": {
    "freePlanOffer": {
      "badge": "Free plan",
      "title": "Start on the Free plan",
      "noCard": "Free of charge. No credit card required.",
      "body": "To try Kirigami, your account needs an active plan. The Free plan gives you {credits} credits and includes:",
      "hint": "If you decline now, you can still subscribe to the Free plan or a paid plan later on the pricing page.",
      "accept": "Activate the Free plan",
      "retry": "Try again",
      "decline": "Not now",
      "pending": "Activating the Free plan",
      "error": "The Free plan could not be activated. Please try again."
    }
  },
  "landing": {
    "skipToFooter": "Skip to footer",
    "hero": {
      "eyebrow": "Scientific writing for researchers",
      "titleLine1": "You know your",
      "titleLine2": "research better than any tool.",
      "subtitleLine1": "You write the science.",
      "subtitleLine2": "Kirigami guides the structure.",
      "primaryCta": "try for free"
    },
    "stage": {
      "sectionAria": "Product features",
      "tablistAria": "Kirigami features",
      "pause": "Pause the tour",
      "play": "Play the tour",
      "shotLabel": "Screenshot {n} of {total}",
      "slides": {
        "write": {
          "name": "Write",
          "bullets": {
            "feedback": "Feedback on the content, structure and coherence of each section: what is wrong, why it matters and what to do.",
            "method": "Based on a method: a human-curated framework of scientific writing guidelines, applied to every section.",
            "reanalyse": "Edit your text in Kirigami and analyse again only the section you changed."
          },
          "shots": {
            "overview": "The Introduction in Write, its text blurred, with the Feedback panel listing its five parts and its links to other sections.",
            "card": "An open feedback card for the Impact of the Introduction: the problem, why it matters and what to do, with Dismiss and Done."
          }
        },
        "read": {
          "name": "Read",
          "bullets": {
            "notes": "Highlight a PDF and write your notes beside it, with the citation ready to copy.",
            "saveFor": "Save each highlight for the manuscripts and sections where you will use it.",
            "notebook": "The Notebook keeps your highlights by section, one click from your text."
          },
          "shots": {
            "notes": "A paper open in the reader at full width with a blue highlight, and its highlights and notes on the right, each linked to a section of a manuscript. The text is blurred.",
            "inWrite": "Write with the reading notes of the Introduction open beside the text: the paper, a highlight and its note, with Cite highlight and Insert note. The text is blurred.",
            "notebook": "The Notebook of a manuscript: its sections on the left and, under the Introduction, two highlights with their notes and citations, blurred."
          }
        },
        "search": {
          "name": "Search",
          "bullets": {
            "ask": "Ask a question: the answer comes from your papers, with the page behind each point.",
            "verify": "Verify a statement and see the passages in your papers that bear on it.",
            "literature": "Search seven public databases at once and save what you find."
          },
          "shots": {
            "verify": "Verify a statement across all libraries: 15 references found, each with its library and page. The statement and the papers are blurred.",
            "sources": "The sources of an answer, numbered, each with its library and page. The papers and passages are blurred.",
            "literature": "Literature results from external databases, with open access, citations, the databases that found them, Read and Save to my references. Titles and authors are blurred."
          }
        },
        "library": {
          "name": "Library",
          "bullets": {
            "topics": "Create a library for each project or topic and give it an icon and a colour.",
            "papers": "Keep each paper with its details and your notes, ready to cite as you write with @.",
            "shared": "See the libraries your lab shares with you next to your own."
          },
          "shots": {
            "topics": "Your libraries, each with its icon and colour, and a library shared with your lab.",
            "new": "New library: an icon and a colour to choose, then its name and description.",
            "papers": "A shared library with its papers: title, authors and journal blurred, then the year, the notes and the date each was added."
          }
        },
        "collaborate": {
          "name": "Collaboration",
          "bullets": {
            "share": "Invite co-authors to read a manuscript and its feedback.",
            "tasks": "Task Management ranks every card and lets you assign each one, with its status.",
            "lab": "Share a library with your lab: members search and read it, only you change it."
          },
          "shots": {
            "share": "Share manuscript: the people with access and an invitation by email.",
            "tasks": "Task Management in Write: the cards by priority, filters by status, section and tag, and Assign on each card.",
            "lab": "Share a library with your lab, everyone in it or chosen members, and whether they may download the PDFs."
          }
        }
      }
    },
    "quote": {
      "text": "I knew the science. But when it came to writing it up, I got blocked. I needed guidance, not a tool to write it for me. So we built Kirigami for every researcher who knows their work but is unsure how to tell its story.",
      "name": "Vânia Silva",
      "role": "Scientist · Co-founder & CEO"
    },
    "encrypted": {
      "lineOnePrefix": "All data submitted to Kirigami is ",
      "highlightOne": "encrypted",
      "highlightTwo": "confidential",
      "highlightThree": "and won't be used for model training"
    },
    "upload": {
      "title": "Your guidance starts here",
      "busy": "An analysis is already in progress. Please wait for it to finish before uploading another paper.",
      "stepsAria": "Upload steps",
      "steps": {
        "draft": {
          "title": "Upload your draft",
          "body": "Create your free account and submit your manuscript as PDF, DOCX or LaTeX, up to 10 MB."
        },
        "analysis": {
          "title": "Structured analysis",
          "body": "Kirigami runs an analysis of your content, section structure and narrative flow through a scientific writing framework. Get your results in minutes."
        },
        "report": {
          "title": "Get your feedback report",
          "body": "Receive a detailed report highlighting strengths, gaps, and clear directions for improvement."
        }
      }
    },
    "uploadnow": {
      "dragDropEmphasis": "Drop your file or",
      "uploadButton": "Upload",
      "infoFiles": "All documents are encrypted",
      "invalidFormatStrong": "Invalid file format !",
      "invalidFormatHint": "Please upload a PDF, DOCX or LaTeX file.",
      "helpType": "Accepted formats: PDF, DOCX and LaTeX. Files over 10 MB are not supported."
    },
    "features": {
      "title": "Why researchers choose Kirigami for scientific writing",
      "pillars": {
        "trust": "Trust",
        "collaboration": "Collaboration",
        "method": "Method"
      },
      "cards": {
        "safety": {
          "title": "Content Safety",
          "body": "All documents are encrypted, confidential and excluded from model training."
        },
        "authorship": {
          "title": "Authorship Integrity",
          "body": "Kirigami gives transparent, traceable suggestions. You keep full author control."
        },
        "noMagic": {
          "title": "No Magic Buttons",
          "body": "Get feedback, edit, re-analyse. You decide every change."
        },
        "share": {
          "title": "Share with Your Team",
          "body": "Invite co-authors to view a manuscript and its feedback. Share libraries with your lab."
        },
        "assign": {
          "title": "Assign the Work",
          "body": "Assign feedback cards to co-authors and follow their status in Task Management."
        },
        "reliability": {
          "title": "Reliability",
          "body": "Kirigami uses a human-curated framework of scientific writing guidelines."
        },
        "learn": {
          "title": "Learn as You Write",
          "body": "Each note explains the problem, why it matters, and reasons through possible fixes."
        },
        "library": {
          "title": "Create Your Library",
          "body": "Ask questions, verify claims. Every answer links to the source page. No hallucinations."
        }
      }
    },
    "faqs": {
      "title": "Frequently asked questions",
      "entries": {
        "whatIs": {
          "question": "What is Kirigami?",
          "answer": "Kirigami is an online tool for researchers writing scientific papers.\nUpload your draft and Kirigami gives you feedback on content, structure and coherence: whether each section contains the information the reader needs to understand your study, whether each section follows the structure journals expect, and whether the argument holds together across the paper.\nEvery note Kirigami gives you explains its reasoning and suggests improvements, so you can weigh it, ignore it, or act on it by editing your manuscript and re-analysing.\nYou can also upload papers, notes, theses or other documents you find relevant to your research and ask questions about them in a closed environment: answers come only from your library and link to the source page, so nothing is invented.\nKirigami never writes for you and never decides for you. It supports your thinking. The judgement is yours."
        },
        "documents": {
          "question": "What kind of documents can I submit?",
          "answer": "PDF, DOCX and LaTeX files, up to 10 MB."
        },
        "howItHelps": {
          "question": "How does Kirigami help me with my paper?",
          "answer": "Upload your draft and Kirigami reads it section by section against a human-curated framework of scientific writing guidelines. It looks at content, structure and coherence: whether each section says what a reader expects, whether it is built the way that section should be, and whether the argument holds from title to conclusion. Each section is scored so you can see where to focus. For every issue, Kirigami names the problem, explains why it matters and reasons through possible fixes. You decide what to change, edit in Kirigami and re-analyse. It takes minutes."
        },
        "writesForMe": {
          "question": "Does Kirigami write my paper for me?",
          "answer": "No. Kirigami reads what you have written and gives you feedback based on the human-curated framework we developed: what information a section may be missing, inconsistencies between your hypothesis and your conclusions, suggestions for structural improvements, and food for thought on what you could consider changing. It never drafts a sentence of your manuscript for you. You write, Kirigami comments, you decide. There is no “write for me” button."
        },
        "hallucinations": {
          "question": "Does Kirigami hallucinate or invent citations?",
          "answer": "No. In Kirigami you build your own library: a knowledge base of the papers, documents and notes you consider useful for your work.\nWhen you ask your knowledge base a question, Kirigami answers only from the papers and documents you have uploaded, never from the open web, and every answer links to the exact page it came from. It cannot cite what it has not read. If Kirigami does not find an answer in your knowledge base, it tells you so.\nYou can also paste a statement and verify it against your own sources. When it reviews your manuscript, every note points to the passage you wrote and explains its reasoning. At any point you can build on it or check it rather than take it on trust."
        },
        "difference": {
          "question": "How is Kirigami different from grammar checkers or generative AI writing tools?",
          "answer": "Grammar tools correct sentences and generative AI produces text. Kirigami does neither. You write the science. Kirigami guides you to improve it."
        },
        "ethics": {
          "question": "Does using Kirigami raise ethical concerns in publishing?",
          "answer": "No. Kirigami does not generate scientific content, so authorship stays with you. Every suggestion is transparent and traceable in your Task Management, should anyone ask."
        },
        "declare": {
          "question": "Do I need to declare Kirigami when I submit to a journal?",
          "answer": "Yes. Declare every AI tool you use. Kirigami comments on your manuscript and never writes it, so a short statement is enough.\nExample: “During the preparation of this manuscript, the authors used Kirigami (kirigami.ai) for feedback on content, structure and coherence. The authors reviewed all suggestions, wrote the final text and take full responsibility for the content.”"
        },
        "dataSafety": {
          "question": "Is my data safe when I use Kirigami?",
          "answer": "Yes. Kirigami is a European company and processes every document under GDPR. It runs on infrastructure certified to ISO 27001 and SOC 2. Documents are encrypted in transit and at rest, access is restricted to the author, and manuscripts are never shared with third parties or used for model training."
        },
        "audience": {
          "question": "Who is Kirigami for?",
          "answer": "PhD students writing their first paper. Postdocs and principal investigators writing their twentieth. Clinicians and medical writers preparing a study for publication. Supervisors and research groups who review drafts before they go out. If you write research papers in the IMRD format (Introduction, Methods, Results, Discussion), in fields such as chemistry, biology or medicine, Kirigami is for you. More formats are coming soon."
        },
        "groups": {
          "question": "Can research groups or institutions use Kirigami?",
          "answer": "Yes.\nWe know that whoever runs a lab, a department or a research office carries two demanding responsibilities at once: the integrity of what the institution publishes, and the training of the people who write it. Kirigami serves both.\nEvery researcher gets guidance whenever they need it and structured feedback to progress with their work, whether or not their supervisor is available at that moment or English is their first language. Kirigami supports researchers without ever writing for them, so what leaves your institution is written by your people.\nSupervisors receive drafts that have already been through structural review, giving them back precious time to focus on discussing the science.\nKirigami offers a set of institutional tools. Shared knowledge bases let the group keep its sources even when people move on. For governance: single sign-on, audit logs and more.",
          "cta": "Talk to us",
          "ctaSuffix": "about your group or institutional licences."
        },
        "freePlan": {
          "question": "Is there a free plan?",
          "answer": "Yes. Subscribe to the free plan and get credits to try everything Kirigami currently does:",
          "bullets": {
            "upload": "Upload your manuscript and get feedback on content, structure and coherence, based on our proprietary scientific writing framework.",
            "library": "Create your library and ask questions or verify statements against the literature you selected.",
            "guidelines": "Check the writing guidelines every time you feel lost.",
            "submit": "Edit, re-analyse and submit with confidence."
          }
        }
      }
    },
    "contact": {
      "title": "Let's talk about your research",
      "subtitle": "We're open to feedback, collaborations and fresh ideas.",
      "cta": "Talk to the team"
    },
    "contactModal": {
      "title": "Let's talk about your research",
      "intro": "Tell us what you are working on. We read every message and reply in one or two working days.",
      "emailLabel": "Email",
      "emailPlaceholder": "you@university.edu",
      "messageLabel": "Message",
      "messagePlaceholder": "Tell us about your research or what you would like to discuss…",
      "attachmentLabel": "Attachment",
      "remove": "Remove",
      "choose": "Choose",
      "attachmentHint": "PNG or JPG image, up to 5 MB.",
      "typeError": "Attachments have to be a PNG or JPG image.",
      "sizeError": "That image is over 5 MB. Please attach a smaller one.",
      "cancel": "Cancel",
      "send": "Send",
      "sending": "Sending…",
      "successToast": "Message sent",
      "successBody": "Thank you for reaching out. We reply in one or two working days.",
      "successWithoutImage": "We have your message. The image did not upload, so please send it to hello@kirigami.ai."
    }
  },
  "pricing": {
    "pageTitle": "Choose Your Plan",
    "audienceAria": "Choose audience",
    "individual": "Individual",
    "team": "Team",
    "billingAria": "Choose billing period",
    "monthly": "Monthly",
    "annual": "Annual",
    "savePill": "2 months free",
    "status": {
      "monthlySelected": "Monthly billing selected. Prices updated.",
      "annualSelected": "Annual billing selected. Prices updated."
    },
    "price": {
      "perMonth": "/ month",
      "perMonthPlusVat": "/ month + VAT",
      "perSeatMonth": "/ seat / month",
      "perSeatMonthPlusVat": "/ seat / month + VAT",
      "noCreditCard": "No credit card required",
      "billedAnnually": "{total}€ billed annually + VAT",
      "seatRange": "{min} to {max} seats",
      "fromMonthlyForSeats": "from {total}€ / month + VAT for {seats} seats",
      "fromAnnualForSeats": "from {total}€ / year + VAT for {seats} seats",
      "credits": "{credits} credits",
      "creditsPerSeat": "{credits} credits per seat"
    },
    "seats": {
      "label": "Seats",
      "decrease": "Fewer seats",
      "increase": "More seats",
      "range": "Minimum {min}, maximum {max}"
    },
    "cta": {
      "startFree": "Start free",
      "subscribe": "Subscribe",
      "contactSales": "Contact sales",
      "contactUs": "Contact us",
      "aria": "{cta} - {plan}"
    },
    "plans": {
      "free": {
        "name": "Free",
        "subtitle": "For a researcher trying Kirigami."
      },
      "researcher": {
        "name": "Researcher",
        "subtitle": "For a researcher writing regularly."
      },
      "researcherPro": {
        "name": "Researcher Pro",
        "subtitle": "For a researcher working on multiple papers."
      },
      "miniLab": {
        "name": "Mini Lab",
        "subtitle": "Teams",
        "orgSize": "For teams of 5 to 20 researchers"
      },
      "researchGroup": {
        "name": "Research Group",
        "subtitle": "Research groups, collaborators",
        "orgSize": "30 researchers"
      },
      "department": {
        "name": "Department",
        "subtitle": "Hospital, institute, university departments",
        "orgSize": "50 researchers"
      },
      "institution": {
        "name": "Institution",
        "subtitle": "Hospital, institute, universities, pharma, consortia",
        "orgSize": "150+ researchers"
      }
    },
    "features": {
      "reportLead": "Kirigami Report:",
      "content": "Content",
      "structure": "Structure",
      "coherence": "Coherence",
      "knowledgeBase": "Knowledge Base: {papers} papers",
      "referenceSearchCapped": "Reference Search on your private Knowledge Base: {queries} queries",
      "referenceSearch": "Reference Search on your private Knowledge Base",
      "shareReports": "Share reports with collaborators",
      "emailSupport": "Email support ({hours}h SLA)",
      "volumeLead": "Higher monthly volume for:",
      "reportAnalysis": "Kirigami Report (Content, Structure & Coherence Analysis)",
      "fullAnalysis": "Full Analysis",
      "reanalysis": "Re-analysis",
      "knowledgeBaseProduct": "Kirigami Knowledge Base",
      "referenceSearchProduct": "Kirigami Reference Search",
      "labKnowledgeBases": "5 knowledge bases per seat, 50 papers each",
      "sharedKnowledgeBases": "Shared knowledge bases across the lab"
    },
    "credits": {
      "title": "How credits work",
      "introCost": "The cost to analyse a paper depends on its length and complexity, so we meter usage in credits.",
      "introMeter": "A live meter shows your balance.",
      "introReanalysis": "Re-analysing just one edited section costs a fraction of a full report.",
      "introRunOut": "Run out credits? Upgrade or Top up.",
      "examples": {
        "fullAnalysis": "Full manuscript analysis",
        "sectionReanalysis": "Section re-analysis",
        "referenceQuery": "Reference search query"
      },
      "costRange": "~{min} to {max} credits",
      "costFlat": "~{credits} credits"
    },
    "trust": {
      "title": "Security & privacy",
      "items": "GDPR-compliant · EU data residency · Never used to train models · Zero-hallucination, grounded only in your knowledge base · You remain the author"
    },
    "teamsLink": {
      "question": "Writing across a team, department or institution?",
      "cta": "See Kirigami for Teams →"
    },
    "foot": {
      "creditFootnote": "Credit costs shown are indicative estimates and may vary with the length and complexity of each document.",
      "disclaimer": "Prices exclude VAT. VAT is calculated at checkout by our payment provider (Paddle) based on your country. Cancel anytime. Unused credits do not roll over. Plans and prices are subject to change."
    },
    "loginGate": {
      "title": "Log in to continue",
      "body": "Choosing a plan is available only to registered users. Log in to continue."
    },
    "seatCheckout": {
      "title": "Subscribe to {plan}",
      "monthly": "Monthly billing",
      "annual": "Annual billing",
      "pricePerSeat": "Price per seat",
      "seats": "Seats",
      "credits": "Credits",
      "creditsValue": "{credits} credits per seat each month",
      "total": "Total",
      "perMonth": "{amount}€ / month",
      "perYear": "{amount}€ / year",
      "totalMonthly": "{amount}€ / month + VAT",
      "totalAnnual": "{amount}€ / year + VAT",
      "equivalentMonthly": "Equivalent to {amount}€ a month",
      "note": "You can add colleagues after payment from Manage subscriptions.",
      "cancel": "Cancel",
      "continue": "Continue to payment"
    },
    "contactSales": {
      "title": "Talk to our team",
      "nameLabel": "Full name",
      "namePlaceholder": "Jane Researcher",
      "emailLabel": "Work email",
      "emailPlaceholder": "jane@university.edu",
      "orgLabel": "Organization",
      "orgPlaceholder": "University / Lab / Company",
      "researchersLabel": "Number of researchers",
      "selectPlaceholder": "Select…",
      "planLabel": "Plan of interest",
      "planNoPreference": "No preference",
      "messageLabel": "How can we help?",
      "messagePlaceholder": "Tell us about your team, your manuscripts, and what you’re looking for.",
      "cancel": "Cancel",
      "cancelAria": "Cancel contact sales",
      "send": "Send message",
      "sendAria": "Send message to sales",
      "success": "Thanks, your message has been received. The Kirigami team will get back to you soon.",
      "errorFallback": "Failed to submit. Please try again."
    }
  }
}
